/**
 * Claract migration redirect for the PUBLIC chats (bd ai-hub-d10j.6).
 *
 * When `/datasets/:id/access` reports `migrated_to` (the dataset's tenant was
 * moved to Claract), both public frontends — the legacy `frontend/` chat and
 * the v2 `public-chat` project — replace the current page with the Claract
 * public chat for the same assistant. Most assistant ids are unchanged by the
 * migration (UUIDs are carried 1:1); a dataset the migrator re-keyed (deduped)
 * comes with `migrated_dataset_id` from `/access`, and then that id is used.
 *
 * Claract routes (ai-hub `askdata-public-chat.routes.ts`):
 *   /askdata/a/:assistantId
 *   /askdata/a/:assistantId/c/:conversationId
 * and its chat host reads `?embedded=` / `?widget=` exactly like ours, so the
 * flags are carried verbatim (an explicit `false` is an override too).
 *
 * Shared by both frontends so the two redirects cannot drift.
 */

export interface ClaractPublicChatRedirectOptions {
  /**
   * `/access.migrated_dataset_id`: the Claract id of a re-keyed dataset
   * (auth.migration_id_map, scoped to the dataset's tenant server-side).
   * Null/empty → the legacy id, which Claract kept.
   */
  migratedDatasetId?: string | null;
  /** Conversation to resume (`/c/:conversationId` in the current URL). */
  conversationId?: string | null;
  /** Raw `?embedded=` value of the current URL, if present. */
  embedded?: string | null;
  /** Raw `?widget=` value of the current URL, if present. */
  widget?: string | null;
}

/**
 * Builds the Claract public-chat URL, or null when `migratedTo` is not a
 * usable http(s) base URL (then the legacy chat simply keeps working — a bad
 * marker must never strand visitors on a broken redirect).
 */
export function buildClaractPublicChatUrl(
  migratedTo: string | null | undefined,
  datasetId: string,
  options: ClaractPublicChatRedirectOptions = {},
): string | null {
  if (!migratedTo || !datasetId) return null;
  let base: URL;
  try {
    base = new URL(migratedTo);
  } catch {
    return null;
  }
  if (base.protocol !== 'https:' && base.protocol !== 'http:') return null;

  const assistantId = options.migratedDatasetId?.trim() || datasetId;
  const basePath = base.pathname.replace(/\/+$/, '');
  let path = `${basePath}/askdata/a/${encodeURIComponent(assistantId)}`;
  if (options.conversationId) {
    path += `/c/${encodeURIComponent(options.conversationId)}`;
  }
  const url = new URL(path, base.origin);
  if (options.embedded === 'true' || options.embedded === 'false') {
    url.searchParams.set('embedded', options.embedded);
  }
  if (options.widget === 'true' || options.widget === 'false') {
    url.searchParams.set('widget', options.widget);
  }
  return url.toString();
}

/**
 * Conversation id from a public-chat path (`…/c/<id>`), or null — also for a
 * malformed percent-escape (`/c/%E0`), which would otherwise throw URIError
 * and abort the redirect (and with it the chat init). Without the id the
 * visitor lands on a fresh Claract conversation, which is the safe fallback.
 */
export function conversationIdFromPublicChatPath(pathname: string): string | null {
  const match = pathname.match(/\/c\/([^/?#]+)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/**
 * Embedded / widget chats and the Claract redirect.
 *
 * The customer's embed.js / widget.js posts `omm_cb_init` to the LEGACY
 * origin on every iframe load (`postMessage(…, targetOrigin)` with the legacy
 * URL). Once the iframe has been redirected to Claract the browser drops that
 * message, so Claract's EmbedBridgeService never receives an init and its
 * `parentOrigin` stays null. What that means on the Claract side (ai-hub
 * `public-chat/chat-host` + `public-access-control.service`):
 *
 *  - The assistant id is in the Claract path, so the dataset still loads —
 *    init is not needed for that.
 *  - `parentOrigin` is only used for the same-origin management-preview
 *    bypass; a customer page never qualified for it, so nothing is lost.
 *  - `requireEmbedding` keeps working: the redirect navigates the iframe
 *    itself, so Claract still runs inside the customer's frame.
 *  - `requiredReferrers` FAILS CLOSED: Claract matches it against
 *    `document.referrer`, which after the hop is the legacy chat (origin only
 *    under the default referrer policy), not the customer page, and the
 *    cross-origin parent href is unreadable. The chat would show "blocked".
 *  - Host-supplied client options in `omm_cb_init` (welcome text, css, …)
 *    are used by the legacy chat instead of the server config, but Claract
 *    ignores init whenever the assistant id is in the path — the customer's
 *    overrides would silently disappear.
 *
 * So an embedded chat is redirected only when neither applies; otherwise it
 * keeps serving the legacy chat and logs why. Those customers are on the
 * migration's customer list and have to re-embed with the Claract snippet.
 *
 * The referrer rule is not iframe-specific: a top-level visit that arrived
 * via a link from an allowed page loses its referrer on the hop the same way,
 * so `requiredReferrers` blocks the redirect in every mode.
 */

/** Init-payload keys that only steer embed.js/widget.js placement, never the chat. */
const HOST_PLACEMENT_KEYS = new Set(['ds', 'target', 'widgetTarget', 'ownButton', 'vibrateButton']);

export type ClaractEmbedRedirectBlocker = 'required_referrers' | 'host_options';

export interface ClaractEmbedRedirectContext {
  /** Running in an iframe, or `?embedded=true` / `?widget=true`. */
  embedded: boolean;
  /** `/access.has_required_referrers` — the dataset restricts embedding pages. */
  hasRequiredReferrers?: boolean | null;
  /** Keys of the `omm_cb_init` options the legacy chat received, if any. */
  initOptionKeys?: readonly string[] | null;
}

/**
 * Why the chat must stay on the legacy chat instead of redirecting, or null
 * when the Claract chat works without the init message. Host options only
 * matter when embedded (that is the only way to receive an init).
 */
export function claractEmbedRedirectBlocker(ctx: ClaractEmbedRedirectContext): ClaractEmbedRedirectBlocker | null {
  if (ctx.hasRequiredReferrers) return 'required_referrers';
  if (ctx.embedded && ctx.initOptionKeys?.some((key) => !HOST_PLACEMENT_KEYS.has(key))) return 'host_options';
  return null;
}

/** Logs a skipped embedded redirect so the case is visible in the customer's console. */
export function logClaractEmbedRedirectSkipped(datasetId: string, blocker: ClaractEmbedRedirectBlocker): void {
  console.info(
    `[claract-redirect] assistant ${datasetId} was migrated to Claract, but this embed stays on the legacy chat ` +
      `(${blocker}): the Claract chat would not see the embedding page through the legacy embed code. ` +
      'Re-embed with the Claract snippet to switch.',
  );
}

/**
 * True when the public chat runs embedded: inside an iframe (a cross-origin
 * `window.top` access throws, which also means embedded) or with an explicit
 * `?embedded=true` / `?widget=true`.
 */
export function isEmbeddedPublicChat(win: Window = window): boolean {
  const params = new URLSearchParams(win.location.search);
  if (params.get('embedded') === 'true' || params.get('widget') === 'true') return true;
  try {
    return win.self !== win.top;
  } catch {
    return true;
  }
}
